import type { Data } from '@strapi/types'
import AppLink from '@/components/elementary/AppLink'
import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer'
import { ArrowRight } from '@/components/icons/ArrowRight'
import { BasicImage } from '@/components/page-builder/components/BasicImage'
import { Paragraph } from '@/components/typography/Paragraph'
import { cn } from '@/lib/styles'
import { NAVBAR_TEST_IDS } from '@/lib/test-ids'
import styles from './NavbarMenuLink.module.css'

interface FeaturedCardProps {
	readonly card: Data.Component<'layout-utilities.featured-card'>
	readonly className?: string
}

export function FeaturedCard({ card, className }: FeaturedCardProps) {
	const { title, subTitle, link, image } = card
	return (
		<div
			key={card.id}
			className={cn('bg-peach-200 min-w-70 p-5 rounded-xl flex flex-col max-w-fit', className)}
			data-test-id={NAVBAR_TEST_IDS.FEATURED_CARD}
		>
			<Paragraph className="mb-4 lg:mb-2.5 text-neutral-500" fontSize="headingSubtitle">
				{title}
			</Paragraph>
			<div className="relative rounded-sm overflow-hidden aspect-[314/209] min-h-52.5 bg-neutral-100 mb-4 lg:mb-6">
				<BasicImage fill component={image} className="object-contain p-5" />
			</div>
			<div className="max-w-70 flex flex-col h-full">
				<Paragraph className="uppercase mb-2 lg:mb-3" fontSize="bodyRegularMedium">
					{subTitle}
				</Paragraph>
				<CkEditorCSRRenderer className="mb-2 lg:mb-4" htmlContent={card.description} />

				{link?.href && link?.label ? (
					<AppLink
						href={link.href}
						variant="primaryLink"
						underline
						className="w-fit mt-auto"
						iconRight={<ArrowRight className="size-3 lg:size-4 text-brand-black" />}
						data-test-id={NAVBAR_TEST_IDS.FEATURED_CARD_LINK}
						openExternalInNewTab={link.newTab ?? false}
					>
						<Paragraph className={styles['nav-content-menu-arrow-link']}>{link.label}</Paragraph>
					</AppLink>
				) : null}
			</div>
		</div>
	)
}
